import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Grid,
  Divider,
  Slider,
  Typography,
} from '@mui/material';
import { UndoIcon, RedoIcon } from 'components/SVG';
import { Close } from '@mui/icons-material';
import { useReactFlow, useStoreApi } from 'reactflow';
import WorkspaceMenu from 'components/Dashboard/WorkspaceMenu';
import { useWorkspaces } from 'hooks/Workspace/useWorkSpaces';
import {
  useBoardNodesContext,
  useCurrentUser,
  useWorkspaceContext,
} from 'hooks/common';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUpdateApplicationUser } from 'hooks/ApplicationUser';
import { SparkEditableTitle } from 'sharedComponents/UI';
import BoardSearchIconWrapper from './BoardSearchIconWrapper';
import AddBoardNodeOptions from './AddBoardNodeOptions';
import BoardViewMenu from './BoardViewMenu';
import BoardShareButton from './BoardShareButton';
import { v4 as uuid } from 'uuid';
import {
  useAddBoardNode,
  useBoardNodesByBoardID,
  useBoardsByID,
  useRedoBoard,
  useUndoBoard,
  useUpdateBoardsByID,
} from 'hooks/Board';
import BoardOptionsMenu from './BoardOptionsMenu';
import {
  findSafePosition,
  transformAllSparkBoardNodesToRectFlowNodes,
  transformSparkBoardNodeToRectFlowNodes,
  useReactFlowHelpers,
} from '../helper/useReactFlowHelpers';
import {
  BOARD_ID_SELECTORS,
  FIFTY_PERCENT_PADDING,
  MAX_ZOOM,
  MIN_ZOOM,
} from '../helper/constants';
import type { BoardNodes } from 'types/BoardNode';
export const buttonStyles = {
  borderRadius: '10px',
  bgcolor: 'white',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--color-border)',
};

type BoardToolBarProps = {
  onVIewToggle: (type: 'list' | 'canvas') => void;
  onClose?: () => void;
  boardID: string;
  isCanvasView: boolean;
  isBoardBlank?: boolean;
  boardElement?: HTMLDivElement | null;
  setIsAddingNewNode?: React.Dispatch<
    React.SetStateAction<{
      boardNodeID: string | null;
      isLoading: boolean;
    }>
  >;
};
const BoardToolBar = ({
  onVIewToggle,
  onClose,
  boardID,
  isCanvasView,
  isBoardBlank = false,
  boardElement,
  setIsAddingNewNode,
}: BoardToolBarProps) => {
  const { dropdownOptions: workspacesDropdownOptions } = useWorkspaces();
  const { getWorkspace } = useWorkspaceContext();
  const { isProduction } = useEnvironment();
  const { currentUserID } = useCurrentUser();
  const { mutateAsync: updateUser } = useUpdateApplicationUser(currentUserID);
  const { mutateAsync: updateBoard } = useUpdateBoardsByID(boardID);
  const workSpace = getWorkspace();
  const {
    addToReactFlowNodes,
    setAllBoardNodes,
    removeFromReactFlowNodes,
    enableNodeDrag,
  } = useReactFlowHelpers();
  const { mutateAsync: addBoardNode, isLoading: isAddingBoardNode } =
    useAddBoardNode(boardID);
  const { board } = useBoardsByID(boardID);
  const { mutateAsync: redoBoard } = useRedoBoard(boardID);
  const { mutateAsync: undoBoard } = useUndoBoard(boardID);
  const { refetch } = useBoardNodesByBoardID(boardID);
  const store = useStoreApi();
  const { getZoom, zoomTo, fitView } = useReactFlow();
  const [zoomLevel, setZoomLevel] = React.useState<number>(getZoom());
  const { handleAddToNodeSelection } = useBoardNodesContext();
  const { getNodes } = useReactFlow();
  const [currentTitle, setCurrentTitle] = useState(
    board?.attributes.name || ''
  ); // Track current title
  const [initialTitle, setInitialTitle] = useState(
    board?.attributes.name || ''
  ); // Track initial title

  React.useEffect(() => {
    setCurrentTitle(board?.attributes.name || ''); // Sync title when the board changes
    setInitialTitle(board?.attributes.name || '');
  }, [board]);

  React.useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const { transform } = store.getState();
      setZoomLevel(transform[2]);
    });
    return () => unsubscribe();
  }, [store]);

  const handleAddNode = async (nodeTypeID: number) => {
    const nodeID = uuid();
    setIsAddingNewNode?.({ boardNodeID: nodeID, isLoading: true });
    const createdBoardNodeID = `${BOARD_ID_SELECTORS.SPARK_BOARD_NODE}-${nodeID}`;

    const tempBoardNode: BoardNodes = {
      id: nodeID,
      type: 'boardNodes',
      attributes: {
        boardNodeTypeID: nodeTypeID,
        dataURL: '',
        comments: '',
        suggestions: '',
        sortOrder: 0,
        readOnly: false,
        createdAt: '',
        createdBy: '',
        modifiedAt: null,
        modifiedBy: '',
        isDeleted: false,
      },
    } as BoardNodes;

    const nodeToAdd = findSafePosition(
      transformSparkBoardNodeToRectFlowNodes(tempBoardNode, false),
      getNodes() || []
    );

    addToReactFlowNodes(nodeToAdd);
    setTimeout(() => {
      handleAddToNodeSelection(nodeID);
      if (isCanvasView) {
        fitView({
          nodes: [{ id: nodeID }],
          duration: 500,
          padding: FIFTY_PERCENT_PADDING,
        });
      } else {
        const boardNodeElement = document.getElementById(createdBoardNodeID);
        if (boardNodeElement) {
          boardNodeElement.scrollIntoView({ behavior: 'smooth' });
          const editable = boardNodeElement.querySelector('.ce-paragraph');
          //@ts-ignore
          editable && editable.focus();
        }
      }
    }, 1000);
    const boardNode = await addBoardNode({
      boardNodeTypeID: nodeTypeID,
      id: nodeID,
      positionX: nodeToAdd.position.x,
      positionY: nodeToAdd.position.y,
    });

    if (!boardNode) {
      removeFromReactFlowNodes(nodeID);
    } else {
      const currentNodes = getNodes();
      enableNodeDrag(currentNodes);
    }
    setIsAddingNewNode?.({ boardNodeID: null, isLoading: false });
  };

  const handleUndo = async () => {
    await undoBoard();
    const response = await refetch();
    if (response.data) {
      setAllBoardNodes(
        transformAllSparkBoardNodesToRectFlowNodes(response.data.data)
      );
    }
  };
  const handleRedo = async () => {
    await redoBoard();
    const response = await refetch();
    if (response.data) {
      setAllBoardNodes(
        transformAllSparkBoardNodesToRectFlowNodes(response.data.data)
      );
    }
  };

  const handleWorkspaceSelect = (workspaceID: string) => {
    updateUser({ defaultWorkspaceID: workspaceID });
  };

  const handleTitleChange = (title: string) => {
    if (title !== initialTitle) {
      // Only update if the title is different
      updateBoard({ name: title }); // API call to update the board name
      setInitialTitle(title); // Update the initialTitle to the new title
    }
    setCurrentTitle(title); // Update the current title in the UI
  };

  const handleSliderChange = (event: Event, value: number | number[]) => {
    if (typeof value === 'number') {
      const newZoom = (value / 100) * (MAX_ZOOM - MIN_ZOOM) + MIN_ZOOM;
      zoomTo(newZoom);
    }
  };
  const zoomValues = ((getZoom() - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100;

  return (
    <Grid
      container
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      flexWrap='nowrap'
      paddingY={1}
      paddingRight={2}
      paddingLeft={2}
      zIndex={3}
      sx={{
        background:
          'linear-gradient(to top, rgba(255, 255, 255, 0),rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 1))',
      }}
    >
      <Grid item container flexWrap='nowrap' alignItems='center' gap={1}>
        <Box borderRadius='10px' display='flex' sx={buttonStyles}>
          <WorkspaceMenu
            workspaceOptions={workspacesDropdownOptions}
            handleWorkspaceSelect={handleWorkspaceSelect}
            workSpace={workSpace}
            isProduction={isProduction}
            minimumView={true}
          />
          <Divider orientation='vertical' flexItem />
          <Box paddingX={2} paddingLeft={1}>
            <SparkEditableTitle
              value={currentTitle}
              onTitleSave={handleTitleChange}
            />
          </Box>
        </Box>
        <Box sx={{ ...buttonStyles, aspectRatio: 1 }}>
          <AddBoardNodeOptions
            onAdd={handleAddNode}
            isLoading={isAddingBoardNode}
          />
        </Box>
        {!isBoardBlank && (
          <>
            <Box sx={{ ...buttonStyles, aspectRatio: 1 }}>
              <BoardSearchIconWrapper boardElement={boardElement} />
            </Box>
            {/* <SparkButton
              sx={{
                border: (theme) => `1px solid ${theme.palette.borderColor}`,
                textTransform: 'none',
                bgcolor: '#fff',
              }}
            >
              Read Only
            </SparkButton> */}
          </>
        )}
      </Grid>
      {!isBoardBlank && (
        <Grid
          item
          container
          flexWrap='nowrap'
          alignItems='center'
          justifyContent='flex-end'
          gap={1}
        >
          <Box
            display='flex'
            alignItems='center'
            flexWrap='nowrap'
            gap='4px'
            paddingX='4px'
            sx={buttonStyles}
            paddingLeft={1}
            paddingRight={1}
          >
            <IconButton
              aria-label='UndoIcon'
              onClick={handleUndo}
              size='small'
              sx={{ '&.MuiIconButton-root': { paddingRight: '2px' } }}
            >
              <UndoIcon width='20' />
            </IconButton>
            <IconButton
              aria-label='RedoIcon'
              onClick={handleRedo}
              size='small'
              sx={{ '&.MuiIconButton-root': { paddingLeft: '2px' } }}
            >
              <RedoIcon width='20' />
            </IconButton>
          </Box>
          {isCanvasView && (
            <Box
              sx={{
                ...buttonStyles,
                width: '150px',
                paddingX: 2,
                paddingRight: 1,
              }}
            >
              <Box sx={{ minWidth: 35, mr: 0.5 }}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  fontFamily='Mont-semibold'
                >{`${Math.round(zoomValues)}%`}</Typography>
              </Box>
              <Slider
                size='small'
                aria-label='Zoom'
                value={zoomValues}
                onChange={handleSliderChange}
                min={0}
                max={100}
              />
            </Box>
          )}
          <BoardShareButton boardID={boardID} />

          <Box sx={{ ...buttonStyles, aspectRatio: 1 }}>
            <BoardViewMenu onClick={onVIewToggle} />
          </Box>
          <BoardOptionsMenu
            boardID={boardID}
            containerStyles={{ ...buttonStyles, aspectRatio: 1, width: '40px' }}
          />
          {onClose && (
            <IconButton aria-label='RedoIcon' onClick={onClose} size='small'>
              <Close />
            </IconButton>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default BoardToolBar;
